import React from "react";
import Card from "components/card";
import { Recipe } from "../../types/recipe";
import {
  VISIBLE_NUTRIENTS,
  NutrientTile,
  formatMacroValue,
} from "./nutrientMeta";

interface RecipeNutritionSummaryProps {
  recipe: Recipe;
}

// Stored values are per serving; the tile shows that plus the whole-recipe
// total (per serving × default_num_of_servings).
const formatNutrition = (value?: number, unit = "g", decimals = 1) => {
  if (value === null || value === undefined) return "—";
  return `${formatMacroValue(value, decimals)}${unit}`;
};

const formatCalories = (value?: number) => {
  if (value === null || value === undefined || value === 0) return "—";
  return `${formatMacroValue(value, 0)} kcal`;
};

const totalNutrition = (
  perServing: number | undefined,
  servings: number
): number | undefined => {
  if (perServing === null || perServing === undefined) return undefined;
  return perServing * servings;
};

// Total across all servings.
const totalLabel = (
  perServing: number | undefined,
  servings: number,
  unit = "g",
  decimals = 1
) => {
  const total = totalNutrition(perServing, servings);
  if (total === null || total === undefined) return "—";
  return `${formatMacroValue(total, decimals)} ${unit} total`;
};

const RecipeNutritionSummary: React.FC<RecipeNutritionSummaryProps> = ({
  recipe,
}) => {
  const servings = recipe.default_num_of_servings ?? 1;

  return (
    <Card extra="w-full p-3 md:p-6">
      <div className="mb-6 flex items-center justify-between gap-3">
        <h2 className="text-xl font-bold text-navy-700 dark:text-white">
          Nutrition Information
        </h2>
        <span className="inline-flex shrink-0 items-center rounded-md bg-biafit-primary/10 px-3 py-1 text-sm font-medium text-biafit-primary dark:bg-biafit-primary/20">
          {recipe.default_num_of_servings}{" "}
          {recipe.default_num_of_servings === 1 ? "serving" : "servings"}
        </span>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        {VISIBLE_NUTRIENTS.map((meta) => (
          <NutrientTile
            key={meta.key}
            meta={meta}
            className="p-4"
            iconClassName="mx-auto mb-2 h-6 w-6 md:h-9 md:w-9"
          >
            <div className="text-2xl font-bold text-navy-700 dark:text-white">
              {meta.key === "calories"
                ? formatCalories(recipe.calories)
                : formatNutrition(recipe[meta.key], meta.unit, meta.decimals)}
            </div>
            <div className={`mb-4 text-sm font-bold ${meta.accent}`}>
              {meta.label}
            </div>
            <div className="mt-1 text-sm font-medium text-gray-700 dark:text-gray-300">
              {totalLabel(recipe[meta.key], servings, meta.unit, meta.decimals)}
            </div>
          </NutrientTile>
        ))}
      </div>
    </Card>
  );
};

export default RecipeNutritionSummary;
